import React, { useState } from "react"
import { Box, Button, Flex, Text } from "@chakra-ui/react"
import { GiftCardTransactions } from "@app/hooks/useGiftcard"
import { graphql, useStaticQuery } from "gatsby"

type Props = {
  transactions: Array<GiftCardTransactions>
}

const pageSize = 5

export const ProductGiftcardBalanceTransactions: React.FC<Props> = ({ transactions }) => {
  const { giftcard } = useStaticQuery<GatsbyTypes.ProductGiftcardBalanceTransactionsQuery>(graphql`
    query ProductGiftcardBalanceTransactions {
      giftcard: sanityPageGiftcard {
        additionalGiftcardTransactionalList
        additionalGiftcardHeaderDate
        additionalGiftcardHeaderAction
        additionalGiftcardHeaderAmount
        additionalGiftcardHeaderStoreName
        additionalGiftcardHeaderReference
        additionalGiftcardViewMore
        additionalGiftcardViewLess
      }
    }
  `)
  const headers: Headers[] = [
    { key: "transaction_date", displayName: giftcard?.additionalGiftcardHeaderDate || "", flexValue: "5.2" },
    { key: "transaction_type", displayName: giftcard?.additionalGiftcardHeaderAction || "", flexValue: "4" },
    { key: "amount", displayName: giftcard?.additionalGiftcardHeaderAmount || "", flexValue: "3" },
    { key: "store_code", displayName: giftcard?.additionalGiftcardHeaderStoreName || "", flexValue: "8" },
    { key: "transaction_reference", displayName: giftcard?.additionalGiftcardHeaderReference || "", flexValue: "5.2" },
  ]
  const [viewMoreTransactions, setViewMoreTransactions] = useState(false)
  const transactionsList = transactions.slice(0, viewMoreTransactions ? transactions.length : pageSize)
  return (
    <Flex direction="column" w="full" my={12} borderTop="1px solid" borderColor="mid.grey" pt="6">
      <Text fontSize="xs" textAlign="center" letterSpacing="broad" lineHeight={6} fontWeight={500}>
        {giftcard?.additionalGiftcardTransactionalList}
      </Text>
      <Box mx={{ base: 0, lg: 5 }} my={{ base: 0, lg: 10 }}>
        {/* Desktop Table headers */}
        <Flex
          direction="row"
          display={{ base: "none", lg: "flex" }}
          pb={3}
          mb={3}
          borderBottom={{ base: "none", lg: "1px" }}
          borderColor="#1C1F2A"
        >
          {headers.map(({ displayName, flexValue }) => (
            <Box flex={flexValue} key={displayName} textAlign={"left"}>
              <Text fontSize="xs" letterSpacing="generous" fontWeight={500}>
                {displayName}
              </Text>
            </Box>
          ))}
        </Flex>
        {transactionsList?.map((transaction, index) => (
          <Flex
            key={index}
            direction={{ base: "column", lg: "row" }}
            py={{ base: 4, lg: 0 }}
            borderBottom={{ base: "1px", lg: "none" }}
            borderColor="#A4A5AA"
            flex={1}
          >
            {headers.map(({ key, displayName, flexValue }) => {
              const data: string = transaction[key]
              // declare display headers on mobile, hidden on desktop
              const mobileHeader = (
                <Box key={`${key}-header`} display={{ base: "flex", lg: "none" }} textAlign="left" width="45%">
                  <Text fontSize="xs" fontWeight={500} lineHeight={6} letterSpacing="generous">
                    {displayName}
                  </Text>
                </Box>
              )
              // declare display cell content on both desktop and mobile
              const cellContent = (
                <Box flex={[1, null]} key={key} wordBreak="break-all" textAlign="left">
                  <Text fontSize="xs" lineHeight={6}>
                    {data || "N/A"}
                  </Text>
                </Box>
              )
              // return one row of each records
              return (
                <Flex flex={[null, flexValue]} key={`${key}-wrapper`} direction="row">
                  {mobileHeader}
                  {cellContent}
                </Flex>
              )
            })}
          </Flex>
        ))}
      </Box>
      {/* Table button */}
      {pageSize < transactionsList.length && (
        <Box textAlign={"center"} mt={{ base: 4, lg: 0 }}>
          <Button
            fontSize="xs"
            onClick={() => setViewMoreTransactions(!viewMoreTransactions)}
            variant="underline"
            letterSpacing="wider"
            lineHeight={7}
          >
            {viewMoreTransactions ? giftcard?.additionalGiftcardViewLess : giftcard?.additionalGiftcardViewMore}
          </Button>
        </Box>
      )}
    </Flex>
  )
}

type Headers = {
  key: keyof GiftCardTransactions
  displayName: string
  flexValue: string
}
